import { createContext, useContext, useEffect, useState, useRef, ReactNode } from 'react';
import { User, Session, AuthError } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { log } from '@/lib/prodLogger';
import { lovable } from '@/integrations/lovable';
import { useNavigate } from 'react-router-dom';
import { setUserContext } from '@/lib/sentry';

/** Standardized auth result type - replaces `{ error: any }` */
interface AuthResult {
  error: AuthError | null;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  signUp: (email: string, password: string, fullName: string) => Promise<AuthResult>;
  signIn: (email: string, password: string) => Promise<AuthResult>;
  signInWithGoogle: () => Promise<AuthResult>;
  signOut: () => Promise<void>;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Helper functions for referral tracking
const VISITOR_ID_KEY = 'exeibits_visitor_id';

function getOrCreateVisitorId(): string {
  let visitorId = localStorage.getItem(VISITOR_ID_KEY);
  if (!visitorId) {
    visitorId = crypto.randomUUID();
    localStorage.setItem(VISITOR_ID_KEY, visitorId);
  }
  return visitorId;
}

async function convertReferral(userId: string) {
  const visitorId = getOrCreateVisitorId();
  
  try {
    const { data: referral } = await supabase
      .from('affiliate_referrals')
      .select('id')
      .eq('visitor_id', visitorId)
      .is('referred_user_id', null)
      .limit(1)
      .maybeSingle();

    if (referral) {
      await supabase
        .from('affiliate_referrals')
        .update({ referred_user_id: userId, converted_at: new Date().toISOString() })
        .eq('id', referral.id);
    }
  } catch (error) {
    // Non-critical: referral tracking failure should not block auth flow
    log.error('[AuthContext] Failed to convert referral', error);
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const convertedUserIds = useRef<Set<string>>(new Set());

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);

        // Set Sentry user context
        if (session?.user) {
          setUserContext({ id: session.user.id, email: session.user.email });
        } else {
          setUserContext(null);
        }

        // Convert referral when user signs up (SIGNED_IN event with new user)
        if (event === 'SIGNED_IN' && session?.user) {
          const userId = session.user.id;
          // Only convert once per user per session
          if (!convertedUserIds.current.has(userId)) {
            convertedUserIds.current.add(userId);
            // Defer the Supabase call to avoid deadlock
            setTimeout(() => {
              convertReferral(userId);
            }, 0);
          }
        }
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      
      // Set Sentry user context on initial load
      if (session?.user) {
        setUserContext({ id: session.user.id, email: session.user.email });
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, fullName: string) => {
    const redirectUrl = `${window.location.origin}/verify-email`;
    
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          full_name: fullName
        }
      }
    });
    
    // Check if email confirmation is required
    // If user exists but session is null, email confirmation is needed
    if (!error && data.user && !data.session) {
      // User needs to verify email - redirect to verification page
      navigate(`/verify-email?email=${encodeURIComponent(email)}`);
    } else if (!error && data.session) {
      // Auto-confirm is enabled, go straight to dashboard
      navigate('/app');
    }
    
    return { error };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    
    if (!error) {
      navigate('/app');
    }
    
    return { error };
  };

  const signInWithGoogle = async () => {
    const result = await lovable.auth.signInWithOAuth("google", {
      redirect_uri: window.location.origin,
    });
    
    return { error: result.error ? (result.error as AuthError) : null };
  };

  const signOut = async () => {
    // Clear sensitive localStorage items for privacy on shared devices
    localStorage.removeItem(VISITOR_ID_KEY);
    localStorage.removeItem('anonymous_journey_id');
    localStorage.removeItem('faq_session_id');
    localStorage.removeItem('download-later-offline-queue');
    localStorage.removeItem('download-later-cache');
    
    // Clear Sentry user context
    setUserContext(null);
    
    await supabase.auth.signOut();
    navigate('/auth');
  };

  return (
    <AuthContext.Provider value={{ user, session, signUp, signIn, signInWithGoogle, signOut, loading }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
